import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift, Minus, Plus, Trash2, Tag, X, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, inr, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";

export default function CartPage() {
  const { user } = useAuth();
  const { cart, updateQty, removeItem, fetchCart } = useShop();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [busy, setBusy] = useState(false);

  const applyCoupon = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Sign in to apply coupons");
      return nav("/auth", { state: { from: "/cart" } });
    }
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal: cart.subtotal });
      setCoupon(data);
      toast.success(`Coupon applied — you saved ${inr(data.discount)}`);
      fetchCart();
    } catch (err) {
      setCoupon(null);
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const removeCoupon = async () => {
    try {
      await api.post("/coupons/remove");
    } catch { /* noop */ }
    setCoupon(null);
    setCode("");
    fetchCart();
  };

  const discount = coupon?.discount || 0;
  const deliveryEstimate = cart.subtotal - discount >= 999 ? 0 : 79;
  const total = Math.max(cart.subtotal - discount + (cart.items.length ? deliveryEstimate : 0), 0);
  const hasBlocked = cart.items.some((i) => i.unavailable || (i.stock !== undefined && i.stock < i.qty));

  if (!cart.items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="empty-cart">
        <div className="w-20 h-20 mx-auto rounded-3xl bg-[#F5EFE6] grid place-items-center"><Gift className="text-[#B45309]" size={34} /></div>
        <h1 className="font-serif text-3xl font-bold text-stone-900 mt-6">Your cart is waiting for something special</h1>
        <p className="text-stone-500 mt-3">Beautiful gifts for every moment are one click away.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors" data-testid="empty-cart-explore">
          Explore Gifts <ArrowRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-14" data-testid="cart-page">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">Your Cart</h1>
      <p className="text-stone-500 mt-1">{cart.items.length} gift{cart.items.length > 1 ? "s" : ""}, wrapped and almost ready.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 mt-10">
        <div className="space-y-4">
          {cart.items.map((item, idx) => (
            <div key={`${item.product_id}-${idx}`} className="flex gap-4 p-4 rounded-2xl bg-white border border-[#E9E2D7]" data-testid={`cart-item-${item.product_id}`}>
              <Link to={`/product/${item.product_id}`} className="shrink-0">
                {item.image ? <img src={item.image} alt={item.name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover" /> : <div className="w-24 h-24 rounded-xl bg-[#F5EFE6]" />}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Link to={`/product/${item.product_id}`} className="font-semibold text-stone-800 text-sm hover:text-[#B45309] transition-colors line-clamp-1">{item.name}</Link>
                    {item.flash && <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[#B45309]/10 text-[#B45309] text-[10px] font-bold uppercase tracking-wide">Flash Sale price</span>}
                    {item.personalization?.recipient_name && <p className="text-xs text-stone-500 mt-1">For: {item.personalization.recipient_name}{item.personalization.message ? ` · "${item.personalization.message}"` : ""}</p>}
                    {item.personalization?.photo_data && (
                      <div className="flex items-center gap-2 mt-1.5" data-testid={`cart-photo-${item.product_id}`}>
                        <img src={item.personalization.photo_data} alt="Personalization upload" className="w-9 h-9 rounded-lg object-cover border border-stone-200" />
                        <span className="text-[11px] text-stone-400 truncate max-w-[140px]">{item.personalization.photo_name || "Photo attached"}</span>
                      </div>
                    )}
                    {item.warning && <p className="flex items-center gap-1 text-xs font-semibold text-amber-700 mt-1.5"><AlertTriangle size={12} /> {item.warning}</p>}
                  </div>
                  <button onClick={() => removeItem(item)} className="p-2 text-stone-400 hover:text-red-600 transition-colors" aria-label="Remove" data-testid={`cart-remove-${item.product_id}`}><Trash2 size={16} /></button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-stone-200 rounded-full">
                    <button onClick={() => updateQty(item, item.qty - 1)} className="w-8 h-8 grid place-items-center text-stone-600 hover:text-[#B45309]" aria-label="Decrease" data-testid={`cart-minus-${item.product_id}`}><Minus size={13} /></button>
                    <span className="w-7 text-center text-sm font-bold">{item.qty}</span>
                    <button onClick={() => updateQty(item, item.qty + 1)} disabled={item.stock !== undefined && item.qty >= item.stock} className="w-8 h-8 grid place-items-center text-stone-600 hover:text-[#B45309] disabled:opacity-30" aria-label="Increase" data-testid={`cart-plus-${item.product_id}`}><Plus size={13} /></button>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-stone-900">{inr(item.line_total)}</p>
                    {item.original_price > item.price && <p className="text-xs text-stone-400 line-through">{inr(item.original_price * item.qty)}</p>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="lg:sticky lg:top-28 h-fit bg-white rounded-2xl border border-[#E9E2D7] p-6" data-testid="cart-summary">
          <h2 className="font-serif text-xl font-bold text-stone-900">Order Summary</h2>
          {coupon ? (
            <div className="flex items-center justify-between mt-5 px-4 py-3 rounded-xl bg-[#78866B]/10 border border-[#78866B]/25">
              <span className="flex items-center gap-2 text-sm font-bold text-[#5c6b52]"><Tag size={14} /> {coupon.code} <span className="font-normal text-stone-500">−{inr(coupon.discount)}</span></span>
              <button onClick={removeCoupon} aria-label="Remove coupon" data-testid="coupon-remove"><X size={15} className="text-stone-400 hover:text-red-600" /></button>
            </div>
          ) : (
            <form onSubmit={applyCoupon} className="flex gap-2 mt-5">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code (try CGO10)" className="flex-1 h-11 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309] uppercase" data-testid="coupon-input" />
              <button disabled={busy || !code.trim()} className="h-11 px-4 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-[#B45309] transition-colors disabled:opacity-50" data-testid="coupon-apply">Apply</button>
            </form>
          )}
          <div className="space-y-2.5 mt-6 text-sm">
            <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#5c6b52] font-semibold"><span>Coupon savings</span><span>−{inr(discount)}</span></div>}
            <div className="flex justify-between text-stone-600"><span>Delivery (estimated)</span><span>{deliveryEstimate === 0 ? <span className="text-[#5c6b52] font-semibold">FREE</span> : inr(deliveryEstimate)}</span></div>
            <div className="border-t border-stone-100 pt-3 flex justify-between text-base font-bold text-stone-900"><span>Total</span><span data-testid="cart-total">{inr(total)}</span></div>
          </div>
          <p className="text-[11px] text-stone-400 mt-2">Final delivery charge is confirmed at checkout based on your pincode.</p>
          <button
            onClick={() => (user ? nav("/checkout") : nav("/auth", { state: { from: "/checkout" } }))}
            disabled={hasBlocked}
            className="mt-6 w-full h-12 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] hover:-translate-y-0.5 hover:shadow-lg transition-[background-color,transform,box-shadow] duration-300 disabled:opacity-50 disabled:hover:translate-y-0"
            data-testid="cart-checkout-btn"
          >
            {hasBlocked ? "Fix unavailable items first" : "Proceed to Checkout"}
          </button>
          <Link to="/shop" className="block text-center text-sm font-semibold text-[#B45309] mt-4 hover:underline" data-testid="cart-continue-shopping">Continue Shopping</Link>
        </aside>
      </div>
    </div>
  );
}
